import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How it Works"
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "how-it-works"
    }}>{`How it Works`}</h1>
    <p>{`Theme UI combines several libraries together to form a mini-framework for
styling applications.`}</p>
    <p>{`Theme UI is built with:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://emotion.sh"
        }}>{`Emotion`}</a>{`: used to generate isolated CSS with theming context`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://mdxjs.com"
        }}>{`MDX`}</a>{`: used to provide custom Emotion styled components to MDX documents,
without polluting the global CSS scope`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/KyleAMathews/typography.js"
        }}>{`Typography.js`}</a>{`: optionally used for creating rich typographic styles with a
simple, high-level API`}</li>
    </ul>
    <h2 {...{
      "id": "themeprovider"
    }}>{`ThemeProvider`}</h2>
    <p>{`The Theme UI `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` component is a wrapper around MDX's `}<inlineCode parentName="p">{`MDXProvider`}</inlineCode>{`,
which adds custom React components to context, and Emotion's `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{`,
which adds the `}<inlineCode parentName="p">{`theme`}</inlineCode>{` object to context for use with Emotion.`}</p>
    <p>{`The Theme UI `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` includes a default `}<inlineCode parentName="p">{`components`}</inlineCode>{` object with styled
components that pick up values from the `}<inlineCode parentName="p">{`theme.styles`}</inlineCode>{` object. For example,
`}<inlineCode parentName="p">{`theme.styles.h1`}</inlineCode>{` will be used in its `}<inlineCode parentName="p">{`components.h1`}</inlineCode>{` component and rendered in
MDX documents.`}</p>
    <p>{`For illustrative purposes, the `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` renders something like the
following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// for demonstration only – this does not reflect the actual source code
import { MDXProvider } from '@mdx-js/react'
import { ThemeProvider, useTheme } from '@emotion/react'

const themeUIComponents = {
  h1: (props) => <h1 css={useTheme().styles.h1} {...props} />,
  h2: (props) => <h2 css={useTheme().styles.h2} {...props} />,
  // ... other MDX components
}

export default ({ components, theme, children }) => (
  <ThemeProvider theme={theme}>
    <MDXProvider components={merge(themeUIComponents, components)}>
      {children}
    </MDXProvider>
  </ThemeProvider>
)
`}</code></pre>
    <h2 {...{
      "id": "custom-components"
    }}>{`Custom Components`}</h2>
    <p>{`When using the `}<inlineCode parentName="p">{`components`}</inlineCode>{` prop to add custom MDX components, each component is
wrapped with the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop to allow the component to pick up values from the
`}<inlineCode parentName="p">{`theme.styles`}</inlineCode>{` object.`}</p>
    <h2 {...{
      "id": "jsx-pragma"
    }}>{`JSX Pragma`}</h2>
    <p>{`The Theme UI JSX function wraps the Emotion JSX function, converting the `}<inlineCode parentName="p">{`sx`}</inlineCode>{`
prop to a call to `}<inlineCode parentName="p">{`@theme-ui/css`}</inlineCode>{`. The two examples below yield the same
results:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// with Emotion's JSX function
// this is typically handled with the Emotion Babel preset
/** @jsxImportSource @emotion/react */
import { css } from 'theme-ui'

export default (props) => (
  <div
    {...props}
    css={css({
      color: 'white',
      bg: 'primary',
      fontSize: 4,
    })}
  />
)
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// with Theme UI's JSX function
/** @jsxImportSource theme-ui */

export default (props) => (
  <div
    {...props}
    sx={{
      color: 'white',
      bg: 'primary',
      fontSize: 4,
    }}
  />
)
`}</code></pre>
    <Note mdxType="Note">
      <p>{`Confused about this `}<inlineCode parentName="p">{`@jsxImportSource`}</inlineCode>{` comment? Read more about
`}<a parentName="p" {...{
          "href": "/guides/jsx-pragma"
        }}>{`JSX Pragma`}</a>{`.`}</p>
    </Note>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      